import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ServerTokenService } from '@rocksoft/mdm-dep-sdk';
import { CertificateService, MdmVendorService, VppTokenService } from '@rocksoft/mdm-sdk-v2';
import { mergeMap, tap } from 'rxjs/operators';
import { HttpRequestService } from './http-request.service';
import {
  ManagementTokensVppResolverActions,
  ManagementVppUiActions,
  ManagementTokensVppApiActions,
  ManagementTokensDepResolverActions,
  ManagementDepUiActions,
  ManagementTokensDepApiActions,
  DepApiActions,
  ManagementTokensPushResolverActions,
  ManagementTokensPushCsrActions,
  ManagementPushUiActions,
  ManagementTokensPushApiActions,
  PushApiActions,
  ManagementTokensDepCertificateActions,
  DepCertificateApiActions,
  ManagementTokensVendorResolverActions,
  ManagementTokensVendorTokenActions,
  ManagementTokensVendorCsrActions,
  ManagementVendorUiActions,
  ManagementTokensVendorApiActions,
  VendorApiActions,
} from './management.actions';
import { safeMap } from '../effects.helpers';

@Injectable()
export class ManagementTokensEffects {
  constructor(
    private readonly actions: Actions,
    private readonly mdmVppTokenService: VppTokenService,
    private readonly mdmDepTokenService: ServerTokenService,
    private readonly mdmVendorService: MdmVendorService,
    private readonly certificateService: CertificateService,
    private readonly httpRequestService: HttpRequestService
  ) {}

  // Vpp Token
  managementTokenVppRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVppResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmVppTokenService.tokensVppGet('2.0'),
          (x) =>
            ManagementTokensVppApiActions.managementVppTokenLoaded({ payload: { expirationDate: x.expirationDate } }),
          (error) => ManagementTokensVppApiActions.managementVppTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenVppUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementVppUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.mdmVppTokenService.tokensVppPut('2.0', { base64UserToken: new TextDecoder().decode(token) }),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return ManagementTokensVppApiActions.managementVppTokenUpdated({ payload: { expirationDate } });
          },
          (error) => ManagementTokensVppApiActions.managementVppTokenUpdatedError({ error })
        )
      )
    );
  });

  // Dep Token
  managementTokenDepRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensDepResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmDepTokenService.tokensServerTokenGet(),
          (x) =>
            ManagementTokensDepApiActions.managementDepTokenLoaded({ payload: { expirationDate: x.expirationDate } }),
          (error) => ManagementTokensDepApiActions.managementDepTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenDepUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementDepUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.mdmDepTokenService.uploadServerTokenForm(new Blob([token], { type: 'application/pkcs7-mime' })),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return DepApiActions.managementDepTokenUpdated({ payload: { expirationDate } });
          },
          (error) => DepApiActions.managementDepTokenUpdatedError({ error })
        )
      )
    );
  });

  // Push Certificate
  managementTokenPushRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensPushResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.certificateService.getPushCertificateRequest('2.0'),
          (x) =>
            ManagementTokensPushApiActions.managementPushTokenLoaded({ payload: { expirationDate: x.expirationDate } }),
          (error) => ManagementTokensPushApiActions.managementPushTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenPushCsrRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensPushCsrActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.startPushCertificateRequest('2.0', true).pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/x-plist', 'push', 'csr');
            })
          ),
          () => ManagementTokensPushApiActions.managementPushCSRDownloaded(),
          (error) => ManagementTokensPushApiActions.managementPushCSRDownloadedError({ error })
        )
      )
    );
  });

  managementTokenPushUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementPushUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.certificateService.completePushCertificateRequestForm(
            '2.0',
            new Blob([token], { type: 'application/pkcs7-mime' })
          ),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return PushApiActions.managementPushTokenUpdated({ payload: { expirationDate } });
          },
          (error) => PushApiActions.managementPushTokenUpdatedError({ error })
        )
      )
    );
  });

  // Dep Certificate
  managementTokenDepCertificateRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensDepCertificateActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.getPemRequest('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/x-pem-file', 'dep', 'pem');
            })
          ),
          () => DepCertificateApiActions.managementDepCertificateDownloaded(),
          (error) => DepCertificateApiActions.managementDepCertificateDownloadedError({ error })
        )
      )
    );
  });

  // Vendor Certificate
  managementTokenVendorRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmVendorService.getCertificate('2.0'),
          (x) =>
            ManagementTokensVendorApiActions.managementVendorTokenLoaded({
              payload: { expirationDate: x.expirationDate },
            }),
          (error) => ManagementTokensVendorApiActions.managementVendorTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenVendorTokenDownloadRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorTokenActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.downloadCer('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/octet-stream', 'vendor', 'pfx');
            })
          ),
          () => ManagementTokensVendorApiActions.managementVendorTokenDownloaded(),
          (error) => ManagementTokensVendorApiActions.managementVendorTokenDownloadedError({ error })
        )
      )
    );
  });

  managementTokenVendorCsrDownloadRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorCsrActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.startVendorCertificateRequest('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/octet-stream', 'vendor', 'csr');
            })
          ),
          () => ManagementTokensVendorApiActions.managementVendorCSRDownloaded(),
          (error) => ManagementTokensVendorApiActions.managementVendorCSRDownloadedError({ error })
        )
      )
    );
  });

  managementTokenVendorUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementVendorUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.httpRequestService.completeVendorCertificate('2.0', token),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return VendorApiActions.managementVendorTokenUpdated({ payload: { expirationDate } });
          },
          (error) => VendorApiActions.managementVendorTokenUpdatedError({ error })
        )
      )
    );
  });

  private createDownloadFile(res: Blob, type: string, name: string, extension: string) {
    // Create a new blob to add the file extension
    const blob = new Blob([res], { type: type });
    // Trigger the download
    const uri = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.download = `${name}.${extension}`;
    anchor.href = uri;
    anchor.click();
  }
}
